import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, date, money } from '../lib/api';
import { messageFor } from '../lib/session';
import { Chip, Empty, Field, Loading, Notice } from '../components/ui';

type Customer = {
  id: string; legal_name: string; billing_email: string;
  primary_contact_name: string | null; primary_contact_phone: string | null;
  billing_address_line1: string | null; billing_address_line2: string | null;
  billing_city: string | null; billing_state: string | null;
  billing_postal_code: string | null; billing_country: string | null;
  status: string; created_at: string;
};

type Detail = {
  customer: Customer;
  users: Array<{ id: string; name: string; email: string; status: string; last_login_at: string | null }>;
  projects: Array<{ id: string; name: string; start_date: string; agreement_count: number; open_invoice_count: number; outstanding_cents: number }>;
};

/**
 * A card on file as the server hands it over: Stripe's reference plus the
 * details a person needs to recognise it. There is no number to show because
 * there is no number anywhere in this system — the card lives in Stripe.
 */
type SavedCard = {
  id: string; brand: string; last4: string;
  expMonth: number; expYear: number;
  funding: string | null; country: string | null; addedAt: string;
};

const BRANDS: Record<string, string> = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express',
  discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay',
};
const brandLabel = (brand: string): string => BRANDS[brand] ?? brand;

/** Cards expire at the end of their stated month. */
function cardExpired(card: SavedCard): boolean {
  const endOfMonth = new Date(card.expYear, card.expMonth, 1);
  return endOfMonth.getTime() <= Date.now();
}

/** The address exactly as it will be printed on an invoice, blank lines dropped. */
function addressLines(customer: Customer): string[] {
  return [
    customer.billing_address_line1,
    customer.billing_address_line2,
    [customer.billing_city, customer.billing_state, customer.billing_postal_code].filter(Boolean).join(' ').trim() || null,
    customer.billing_country,
  ].filter((line): line is string => Boolean(line));
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get<Detail>(`/api/admin/customers/${id}`).then(setData).catch((cause) => setError(messageFor(cause))), [id]);
  useEffect(() => { void load(); }, [load]);

  // Cards come from the payment provider, not our database, so they load on
  // their own and a provider hiccup degrades to "unavailable" rather than
  // taking the whole customer page down with it.
  const [cards, setCards] = useState<SavedCard[] | null>(null);
  const [cardsError, setCardsError] = useState(false);
  const loadCards = useCallback(() => {
    setCardsError(false);
    return api.get<{ cards: SavedCard[] }>(`/api/admin/customers/${id}/cards`)
      .then((result) => setCards(result.cards))
      .catch(() => { setCards([]); setCardsError(true); });
  }, [id]);
  useEffect(() => { void loadCards(); }, [loadCards]);

  async function removeCard(card: SavedCard) {
    if (!window.confirm(`Remove ${card.brand} ending ${card.last4}? The customer will have to enter it again to use it.`)) return;
    setError(null); setMessage(null);
    try {
      await api.del(`/api/admin/customers/${id}/cards/${card.id}`);
      setMessage(`Removed ${card.brand} ending ${card.last4}.`);
      await loadCards();
    } catch (cause) { setError(messageFor(cause)); }
  }

  async function requestBillingAddress() {
    setError(null); setMessage(null); setRequesting(true);
    try {
      const result = await api.post<{ sent: string[] }>(`/api/admin/customers/${id}/request-billing-address`);
      setMessage(`Asked ${result.sent.join(', ')} for the billing address.`);
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setRequesting(false);
    }
  }

  async function saveDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = new FormData(event.currentTarget); setError(null); setMessage(null);
    try {
      await api.patch(`/api/admin/customers/${id}`, {
        name: form.get('name'), billingEmail: form.get('billingEmail'),
        contactName: form.get('contactName'), contactPhone: form.get('contactPhone'),
        addressLine1: form.get('addressLine1'), addressLine2: form.get('addressLine2'),
        city: form.get('city'), state: form.get('state'), postalCode: form.get('postalCode'),
        country: String(form.get('country') || 'US').toUpperCase(),
      });
      setMessage('Billing details saved.'); setEditing(false); await load();
    } catch (cause) { setError(messageFor(cause)); }
  }

  async function createProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = new FormData(event.currentTarget); setError(null);
    try {
      const result = await api.post<{ id: string }>('/api/admin/projects', { clientId: id, name: form.get('name'), startDate: form.get('startDate') });
      navigate(`/admin/projects/${result.id}`);
    } catch (cause) { setError(messageFor(cause)); }
  }
  async function inviteCustomer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const formElement = event.currentTarget; const form = new FormData(formElement); setError(null);
    try {
      await api.post(`/api/admin/customers/${id}/users`, { name: form.get('name'), email: form.get('email') });
      setMessage('Customer invitation sent.'); formElement.reset(); await load();
    } catch (cause) { setError(messageFor(cause)); }
  }

  if (!data) return error ? <Notice kind="error">{error}</Notice> : <Loading />;
  const today = new Date().toISOString().slice(0, 10);
  const customer = data.customer;
  const lines = addressLines(customer);
  return (
    <>
      <Link className="back-link" to="/admin/customers">&larr; Customers</Link>
      <header className="simple-page-head"><div><span>Customer</span><h1>{customer.legal_name}</h1><p>{customer.billing_email}</p></div></header>
      {error && <Notice kind="error">{error}</Notice>}{message && <Notice kind="ok">{message}</Notice>}

      <section className="simple-panel section-panel">
        <div className="simple-panel-head">
          <div><span>Billing</span><h2>Billing details</h2></div>
          <button className="secondary" onClick={() => setEditing((value) => !value)}>{editing ? 'Cancel' : 'Edit details'}</button>
        </div>
        {!editing ? (
          <>
            <dl className="detail-grid">
              <div><dt>Billing email</dt><dd>{customer.billing_email}</dd></div>
              <div><dt>Contact</dt><dd>{customer.primary_contact_name || '—'}</dd></div>
              <div><dt>Phone</dt><dd>{customer.primary_contact_phone || '—'}</dd></div>
              <div>
                <dt>Billing address</dt>
                <dd>{lines.length === 0 ? '—' : lines.map((line) => <span key={line}>{line}</span>)}</dd>
              </div>
              <div>
                <dt>Card on file</dt>
                <dd>
                  {cards === null ? 'Loading…'
                    : cardsError ? 'Unavailable right now'
                    : cards.length === 0 ? <span className="muted-inline">None — saved automatically after a card payment</span>
                    : cards.map((card) => (
                        <span key={card.id} className="saved-card">
                          <span>{brandLabel(card.brand)} •••• {card.last4}</span>
                          <small>
                            Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                            {cardExpired(card) && <b className="card-expired"> — expired</b>}
                          </small>
                          <button type="button" className="ghost small" onClick={() => removeCard(card)}>Remove</button>
                        </span>
                      ))}
                </dd>
              </div>
            </dl>
            {!customer.billing_address_line1 && (
              <div className="detail-warning">
                <Notice kind="warn">
                  No billing address yet. Invoices and receipts print without one — add it before the first invoice goes out.
                  {' '}Either enter it above, or ask the customer to add it themselves from their portal.
                </Notice>
                <button type="button" className="secondary small" disabled={requesting}
                        onClick={requestBillingAddress}>
                  {requesting ? 'Sending…' : 'Ask customer for billing address'}
                </button>
              </div>
            )}
          </>
        ) : (
          <form className="inline-create-form" onSubmit={saveDetails}>
            <div className="simple-form-grid">
              <Field label="Customer / company name"><input name="name" required defaultValue={customer.legal_name} /></Field>
              <Field label="Billing email"><input name="billingEmail" type="email" required defaultValue={customer.billing_email} /></Field>
              <Field label="Contact name"><input name="contactName" defaultValue={customer.primary_contact_name ?? ''} /></Field>
              <Field label="Contact phone"><input name="contactPhone" defaultValue={customer.primary_contact_phone ?? ''} /></Field>
            </div>
            <h3 className="form-subhead">Billing address</h3>
            <p className="form-subnote">Printed on every invoice and receipt, and sent to the payment provider.</p>
            <div className="simple-form-grid">
              <Field label="Address line 1"><input name="addressLine1" autoComplete="address-line1" defaultValue={customer.billing_address_line1 ?? ''} /></Field>
              <Field label="Address line 2"><input name="addressLine2" autoComplete="address-line2" defaultValue={customer.billing_address_line2 ?? ''} /></Field>
              <Field label="City"><input name="city" autoComplete="address-level2" defaultValue={customer.billing_city ?? ''} /></Field>
              <Field label="State / region"><input name="state" autoComplete="address-level1" defaultValue={customer.billing_state ?? ''} /></Field>
              <Field label="Postal code"><input name="postalCode" autoComplete="postal-code" defaultValue={customer.billing_postal_code ?? ''} /></Field>
              <Field label="Country" hint="Two-letter code, e.g. US"><input name="country" maxLength={2} autoComplete="country" defaultValue={customer.billing_country ?? 'US'} /></Field>
            </div>
            <div className="form-submit"><button type="submit">Save details</button></div>
          </form>
        )}
      </section>

      <div className="two-column">
        <section className="simple-panel simple-form-panel">
          <h2>Add project</h2><p>Projects keep every agreement and payment in the right context.</p>
          <form onSubmit={createProject}><Field label="Project name"><input name="name" required /></Field><Field label="Start date"><input name="startDate" type="date" defaultValue={today} required /></Field><button type="submit">Create project</button></form>
        </section>
        <section className="simple-panel simple-form-panel">
          <h2>Customer login</h2><p>Invite the customer to access all of their projects.</p>
          <form onSubmit={inviteCustomer}><Field label="Customer name"><input name="name" defaultValue={data.customer.primary_contact_name ?? ''} required /></Field><Field label="Email"><input name="email" type="email" defaultValue={data.customer.billing_email} required /></Field><button type="submit">Send invitation</button></form>
        </section>
      </div>
      <section className="simple-panel section-panel">
        <div className="simple-panel-head"><div><span>Work</span><h2>Projects</h2></div><b>{data.projects.length}</b></div>
        {data.projects.length === 0 ? <Empty title="No projects yet" /> : <div className="customer-list project-admin-list">{data.projects.map((project) => (
          <Link to={`/admin/projects/${project.id}`} key={project.id}>
            <div><strong>{project.name}</strong><span>Started {date(project.start_date)}</span></div>
            <dl><div><dt>Agreements</dt><dd>{project.agreement_count}</dd></div><div><dt>Due</dt><dd>{money(project.outstanding_cents)}</dd></div></dl><b>Manage &rarr;</b>
          </Link>
        ))}</div>}
      </section>
      {data.users.length > 0 && <section className="simple-panel section-panel"><div className="simple-panel-head"><div><span>Access</span><h2>Customer logins</h2></div></div><div className="simple-list">{data.users.map((user) => <article className="simple-list-row" key={user.id}><div className="row-main"><strong>{user.name}</strong><small>{user.email}{user.last_login_at ? ` · Last login ${date(user.last_login_at)}` : ''}</small></div><Chip value={user.status} /></article>)}</div></section>}
    </>
  );
}
